/**
 * Sign in.
 *
 * One button, because there is one way in: the A3RN identity provider's hosted
 * login. Phase 4 retired Cynosure's own email-code and passkey flows along with
 * the endpoints behind them, which is why ~500 lines left this file.
 *
 * The two platforms diverge in exactly one place. On native the browser sheet
 * resolves in-process and hands back the authorization code, so the flow
 * finishes inside `handleIdpLogin`. On web the page navigates away entirely and
 * never comes back — app/auth/callback.tsx picks it up — so `startLogin`
 * resolving with null is the NORMAL path there, not a failure.
 *
 * ⚠️  ON WEB THIS SCREEN USUALLY DOES NOT PAINT. There is one control and one
 *     destination, so it hands the browser to the IdP on mount and the button
 *     below is what remains for the arrivals that must not bounce — after a
 *     logout, and after a failed sign-in. src/auth/autoLogin.ts owns that
 *     decision and explains both. Native keeps the tap on purpose: opening
 *     ASWebAuthenticationSession with no gesture behind it fires the iOS system
 *     consent alert naming auth.a3rn.com unprompted.
 */

import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, View, Text, TouchableOpacity, ScrollView } from 'react-native';
import { useRouter } from 'expo-router';
import KeyboardAwareView from '../../src/components/KeyboardAwareView';
import { getUserFacingMessage } from '../../src/utils/errors';
import { useTheme } from '../../src/theme/ThemeContext';
import { useThemedStyles } from '../../src/theme/useThemedStyles';
import type { Theme } from '../../src/theme/types';
import { openLegalUrl } from '../../src/constants';
import { isOidcEnabled } from '../../src/auth/oidcConfig';
import { startLogin } from '../../src/auth/oidc';
import { completeOidcLogin } from '../../src/auth/completeOidcLogin';
import { shouldAutoLogin } from '../../src/auth/autoLogin';

export default function LoginScreen() {
  const router = useRouter();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolved once: on web it comes from window.__CYNOSURE_CONFIG__, which the
  // backend injects at startup, so it cannot change during a session.
  const idpEnabled = isOidcEnabled();

  // Decided during the first render, not in the effect, so the redirecting case
  // never paints a button for a frame before replacing it. `shouldAutoLogin` is
  // false on native and memoised per page load; short-circuiting on idpEnabled
  // keeps it from consuming the suppression flag on a deployment that has no
  // IdP to redirect to.
  const [autoRedirecting, setAutoRedirecting] = useState(
    () => idpEnabled && shouldAutoLogin(),
  );
  const autoStarted = useRef(false);

  const handleIdpLogin = async () => {
    setError(null);
    setBusy(true);
    try {
      const result = await startLogin();
      if (!result) return; // web navigated away, or the user dismissed the sheet
      await completeOidcLogin(result.code);
      router.replace('/');
    } catch (e) {
      // Drops out of the redirecting view, because the spinner it renders has
      // no way to show this. On web the failure is almost always discovery: the
      // IdP is unreachable, so there is nothing to redirect TO and the button
      // plus the message is the only useful thing left on screen.
      setAutoRedirecting(false);
      setError(getUserFacingMessage(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!autoRedirecting || autoStarted.current) return;
    // Latched rather than relying on the effect's deps: `handleIdpLogin` sets
    // state, and a second navigation started from a re-render would race the
    // first one's PKCE record — the later write wins and the returning code no
    // longer matches the verifier that is stored.
    autoStarted.current = true;
    void handleIdpLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRedirecting]);

  if (autoRedirecting) {
    // Branded rather than a bare spinner: `startLogin` fetches the discovery
    // document before it can navigate, so this is on screen for a round trip on
    // a cold load and should read as the app opening, not as a blank frame.
    return (
      <View style={styles.redirectingContainer} testID="login.auto-redirecting">
        <Text style={styles.logoText}>Cynosure</Text>
        <ActivityIndicator
          size="large"
          color={colors.primary}
          style={styles.redirectingSpinner}
          accessibilityLabel="Opening sign-in"
        />
      </View>
    );
  }

  return (
    <KeyboardAwareView style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        <View style={styles.logoContainer}>
          <Text style={styles.logoText}>Cynosure</Text>
          <Text style={styles.tagline}>Your time, spent on what matters.</Text>
        </View>

        <View style={styles.formContainer}>
          {!idpEnabled ? (
            // Not a user error and not recoverable by trying again — there is no
            // other way to sign in since Phase 4, so this says so plainly rather
            // than showing a button that cannot work.
            <View style={styles.warningBox} testID="login.idp-unavailable">
              <Text style={styles.warningText} accessible={false}>
                Sign-in is unavailable: this app is not configured with an identity provider.
              </Text>
            </View>
          ) : (
            <>
              <TouchableOpacity
                style={[styles.button, styles.primaryButton, busy && styles.buttonDisabled]}
                onPress={handleIdpLogin}
                disabled={busy}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel="Sign in"
                accessibilityState={{ disabled: busy }}
                testID="login.idp-button"
              >
                <Text style={styles.buttonText} accessible={false}>
                  {busy ? 'Opening sign-in…' : 'Sign in'}
                </Text>
              </TouchableOpacity>
              {/* Same destination as Sign in, deliberately. Registration lives
                  BEHIND /authorize on the hosted login — a3rn-fleet's
                  configure.py keeps allowRegister true — so the only route to a
                  new account was a button that reads like it wants one you
                  already have. This is the signpost, not a second flow. */}
              <TouchableOpacity
                style={styles.secondaryButton}
                onPress={handleIdpLogin}
                disabled={busy}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel="Create an account"
                accessibilityState={{ disabled: busy }}
                testID="login.signup-button"
              >
                <Text style={styles.secondaryButtonText} accessible={false}>
                  New here? Create an account
                </Text>
              </TouchableOpacity>
              {error && (
                <View style={styles.errorBox} accessibilityRole="alert" testID="login.idp-error">
                  <Text style={styles.errorText} accessible={false}>
                    {error}
                  </Text>
                </View>
              )}
            </>
          )}
        </View>

        {/* Inline, not a gate. The checkbox that used to stand here was
            component state thrown away on submit — nothing read it, nothing
            stored it — so it re-asked every returning user at every sign-in and
            left no evidence that anyone had ever agreed. Acceptance that is
            actually RECORDED happens in LegalUpdateBanner, against the versioned
            manifest at LEGAL_VERSION_URL, and the hosted login now carries the
            same two links on the page that creates the account (a3rn-fleet
            configure.py, privacy policy). */}
        <View style={styles.consentSection}>
          <Text style={styles.consentText}>
            By continuing, you agree to the{' '}
            <Text
              style={styles.termsLink}
              onPress={() => openLegalUrl('terms')}
              accessibilityRole="link"
              accessibilityLabel="Terms of Service"
              testID="login.terms-link"
            >
              Terms of Service
            </Text>{' '}
            and{' '}
            <Text
              style={styles.termsLink}
              onPress={() => openLegalUrl('privacy')}
              accessibilityRole="link"
              accessibilityLabel="Privacy Policy"
              testID="login.privacy-link"
            >
              Privacy Policy
            </Text>
            .
          </Text>
        </View>
      </ScrollView>
    </KeyboardAwareView>
  );
}

const createStyles = (theme: Theme) => ({
  container: { flex: 1 as const, backgroundColor: theme.colors.bgSecondary },
  redirectingContainer: {
    flex: 1 as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    padding: 24,
    backgroundColor: theme.colors.bgSecondary,
  },
  redirectingSpinner: { marginTop: 24 },
  scrollContent: {
    flexGrow: 1 as const,
    justifyContent: 'center' as const,
    paddingHorizontal: 24,
    paddingVertical: 48,
  },
  logoContainer: { alignItems: 'center' as const, marginBottom: 48 },
  logoText: {
    fontSize: 40,
    fontWeight: '800' as const,
    color: theme.colors.primary,
    letterSpacing: -1,
  },
  tagline: {
    fontSize: 16,
    color: theme.colors.textMuted,
    marginTop: 8,
    textAlign: 'center' as const,
  },
  formContainer: {
    backgroundColor: theme.colors.surface,
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 3,
  },
  button: {
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    flexDirection: 'row' as const,
    minHeight: 48,
    gap: 10,
  },
  primaryButton: { backgroundColor: theme.colors.primary },
  buttonDisabled: { opacity: 0.6 },
  buttonText: { color: theme.colors.textOnPrimary, fontSize: 16, fontWeight: '700' as const },
  // Text-weight, not a second filled button: it goes to the same place as the
  // primary, so it must not compete with it.
  secondaryButton: {
    marginTop: 12,
    paddingVertical: 12,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    minHeight: 44,
  },
  secondaryButtonText: { color: theme.colors.primary, fontSize: 15, fontWeight: '600' as const },
  errorBox: {
    backgroundColor: theme.colors.errorBg,
    borderWidth: 1,
    borderColor: theme.colors.errorBorder,
    borderRadius: 10,
    padding: 14,
    marginTop: 12,
  },
  errorText: { color: theme.colors.errorText, fontSize: 14, lineHeight: 20 },
  warningBox: {
    backgroundColor: theme.colors.warningBg,
    borderWidth: 1,
    borderColor: theme.colors.warningBorder,
    borderRadius: 10,
    padding: 14,
  },
  warningText: { color: theme.colors.warningText, fontSize: 14, lineHeight: 20 },
  consentSection: { marginTop: 24, paddingHorizontal: 4 },
  consentText: {
    fontSize: 13,
    color: theme.colors.textSecondary,
    lineHeight: 20,
    textAlign: 'center' as const,
  },
  termsLink: { color: theme.colors.primary, textDecorationLine: 'underline' as const },
});
